"use client";

import { useEffect, useState } from "react";

import { CONVERGENCE } from "../_lib/event";

interface Parts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const UNITS: { key: keyof Parts; label: string }[] = [
  { key: "days", label: "DAYS" },
  { key: "hours", label: "HOURS" },
  { key: "minutes", label: "MIN" },
  { key: "seconds", label: "SEC" },
];

/** Card height in px, which the glyph inside each half is measured against. */
const CARD = 74;

function remaining(target: number): Parts {
  const ms = Math.max(0, target - Date.now());
  const total = Math.floor(ms / 1000);
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor(total / 3600) % 24,
    minutes: Math.floor(total / 60) % 60,
    seconds: total % 60,
  };
}

function pad(value: number) {
  return String(value).padStart(2, "0");
}

interface Props {
  /** Tone of the surface the countdown sits on. */
  variant?: "light" | "dark";
  className?: string;
}

/**
 * A flip clock. Each card keeps the figure it was showing in its lower half
 * until the new one folds down over it, which is what sells the hinge.
 */
export function Countdown({ variant = "light", className }: Props) {
  // Rendered empty on the server: the value depends on the current clock, so
  // filling it before mount would guarantee a hydration mismatch.
  const [clock, setClock] = useState<{ now: Parts | null; was: Parts | null }>({
    now: null,
    was: null,
  });

  useEffect(() => {
    const target = new Date(CONVERGENCE.dateISO).getTime();
    setClock({ now: remaining(target), was: null });
    const id = setInterval(() => {
      setClock((current) => ({ now: remaining(target), was: current.now }));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  const dark = variant === "dark";
  const face = dark
    ? "border-white/15 bg-white/5 text-white"
    : "border-marketing-border-light text-marketing-text bg-white";
  const hinge = dark ? "bg-white/15" : "bg-marketing-border-light";

  return (
    <div
      className={[
        "inline-flex items-start gap-2 sm:gap-3",
        className ?? "",
      ].join(" ")}
    >
      <style>{`
        .cd-card { perspective: 300px; }
        .cd-half {
          position: absolute;
          left: 0;
          right: 0;
          height: 50%;
          overflow: hidden;
          backface-visibility: hidden;
        }
        .cd-top { top: 0; }
        .cd-bottom { bottom: 0; }
        .cd-glyph {
          position: absolute;
          left: 0;
          right: 0;
          height: ${CARD}px;
          line-height: ${CARD}px;
          text-align: center;
        }
        .cd-top .cd-glyph { top: 0; }
        .cd-bottom .cd-glyph { bottom: 0; }
        .cd-fold-top {
          transform-origin: bottom;
          animation: cd-fold-top 300ms ease-in both;
          z-index: 2;
        }
        .cd-fold-bottom {
          transform-origin: top;
          animation: cd-fold-bottom 300ms 300ms ease-out both;
          z-index: 2;
        }
        @keyframes cd-fold-top {
          from { transform: rotateX(0deg); }
          to { transform: rotateX(-90deg); }
        }
        @keyframes cd-fold-bottom {
          from { transform: rotateX(90deg); }
          to { transform: rotateX(0deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cd-fold-top, .cd-fold-bottom { animation: none; }
          .cd-fold-top { display: none; }
        }
      `}</style>

      {UNITS.map((unit) => {
        const value = clock.now ? pad(clock.now[unit.key]) : "--";
        const previous = clock.was ? pad(clock.was[unit.key]) : value;
        const folding = previous !== value;

        return (
          <div key={unit.key} className="flex flex-col items-center">
            <div
              className={[
                "cd-card relative w-[76px] overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.05)] sm:w-[92px]",
                face,
              ].join(" ")}
              style={{ height: CARD }}
            >
              {/* the figure being turned to, waiting under the leaf that is folding away */}
              <div className="cd-half cd-top">
                <span
                  className="cd-glyph text-[30px] font-medium tabular-nums sm:text-[34px]"
                  style={{ letterSpacing: "-0.03em" }}
                >
                  {value}
                </span>
              </div>
              {/* the figure being turned from, still showing until it is covered */}
              <div className="cd-half cd-bottom">
                <span
                  className="cd-glyph text-[30px] font-medium tabular-nums sm:text-[34px]"
                  style={{ letterSpacing: "-0.03em" }}
                >
                  {previous}
                </span>
              </div>

              {folding ? (
                <>
                  <div
                    key={`top-${value}`}
                    className={["cd-half cd-top cd-fold-top", face].join(" ")}
                  >
                    <span
                      className="cd-glyph text-[30px] font-medium tabular-nums sm:text-[34px]"
                      style={{ letterSpacing: "-0.03em" }}
                    >
                      {previous}
                    </span>
                  </div>
                  <div
                    key={`bottom-${value}`}
                    className={["cd-half cd-bottom cd-fold-bottom", face].join(
                      " ",
                    )}
                  >
                    <span
                      className="cd-glyph text-[30px] font-medium tabular-nums sm:text-[34px]"
                      style={{ letterSpacing: "-0.03em" }}
                    >
                      {value}
                    </span>
                  </div>
                </>
              ) : null}

              <span
                aria-hidden="true"
                className={[
                  "pointer-events-none absolute inset-x-0 top-1/2 z-[3] h-px",
                  hinge,
                ].join(" ")}
              />
            </div>

            <span
              className={[
                "mt-2 text-[10px] font-medium tracking-[0.14em]",
                dark ? "text-white/55" : "text-marketing-text-muted",
              ].join(" ")}
            >
              {unit.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
